import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { dilDegistir, getAktifDil } from '../i18n/i18n';
import * as XLSX from 'xlsx';

interface Dil {
    dilID: number;
    dilKodu: string;
    dilAdi: string;
    varsayilan: boolean;
    aktifMi: boolean;
    siraNo: number;
    ceviriSayisi: number;
}

interface EditorSatir {
    anahtar: string;
    turkceMetin: string;
    hedefMetin: string;
    cevrildi: boolean;
}

const DilYonetimi: React.FC = () => {
    const [diller, setDiller] = useState<Dil[]>([]);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [seciliDilID, setSeciliDilID] = useState<number | null>(null);
    const [editorSatirlar, setEditorSatirlar] = useState<EditorSatir[]>([]);
    const [editorYukleniyor, setEditorYukleniyor] = useState(false);
    const [kayitYapiliyor, setKayitYapiliyor] = useState(false);
    const [toast, setToast] = useState<{ tip: 'ok' | 'hata'; mesaj: string } | null>(null);
    const [filtre, setFiltre] = useState<'tumu' | 'eksik'>('tumu');
    const [aramaMetni, setAramaMetni] = useState('');

    // Yeni dil ekleme formu
    const [yeniDilFormu, setYeniDilFormu] = useState(false);
    const [yeniDilKodu, setYeniDilKodu] = useState('');
    const [yeniDilAdi, setYeniDilAdi] = useState('');
    const [yeniDilSiraNo, setYeniDilSiraNo] = useState(1);

    const [excelYukleniyor, setExcelYukleniyor] = useState(false);
    const excelInputRef = useRef<HTMLInputElement>(null);

    const aktifDil = getAktifDil();

    const toastGoster = (tip: 'ok' | 'hata', mesaj: string) => {
        setToast({ tip, mesaj });
        setTimeout(() => setToast(null), 3000);
    };

    const dilleriYukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const res = await axiosInstance.get('/api/Ceviriler/diller');
            setDiller(res.data);
        } catch {
            toastGoster('hata', 'Diller yüklenemedi.');
        } finally {
            setYukleniyor(false);
        }
    }, []);

    useEffect(() => { dilleriYukle(); }, [dilleriYukle]);

    const editorAc = async (dilID: number) => {
        if (seciliDilID === dilID) { setSeciliDilID(null); return; }
        setSeciliDilID(dilID);
        setEditorYukleniyor(true);
        setAramaMetni('');
        setFiltre('tumu');
        try {
            const res = await axiosInstance.get(`/api/Ceviriler/editor/${dilID}`);
            setEditorSatirlar(res.data.satirlar);
        } catch {
            toastGoster('hata', 'Çeviriler yüklenemedi.');
        } finally {
            setEditorYukleniyor(false);
        }
    };

    const hedefMetinGuncelle = (anahtar: string, deger: string) => {
        setEditorSatirlar(prev => prev.map(s =>
            s.anahtar === anahtar ? { ...s, hedefMetin: deger, cevrildi: deger.trim() !== '' } : s
        ));
    };

    const kaydet = async () => {
        if (!seciliDilID) return;
        setKayitYapiliyor(true);
        try {
            await axiosInstance.post('/api/Ceviriler/kaydet', {
                dilID: seciliDilID,
                satirlar: editorSatirlar.map(s => ({ anahtar: s.anahtar, deger: s.hedefMetin })),
            });
            toastGoster('ok', 'Çeviriler kaydedildi.');
            // Cache'i temizle ve dilleri yenile
            const dil = diller.find(d => d.dilID === seciliDilID);
            if (dil) sessionStorage.removeItem(`yapicrm_ceviriler_${dil.dilKodu}`);
            dilleriYukle();
        } catch {
            toastGoster('hata', 'Kayıt başarısız.');
        } finally {
            setKayitYapiliyor(false);
        }
    };

    const excelIndir = () => {
        const dil = diller.find(d => d.dilID === seciliDilID);
        if (!dil) return;
        const dilAdi = dil.dilAdi;
        const wsData = [
            ['Anahtar', 'Türkçe (Orijinal)', dilAdi],
            ...editorSatirlar.map(s => [s.anahtar, s.turkceMetin, s.hedefMetin]),
        ];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 40 }, { wch: 50 }, { wch: 50 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, dilAdi);
        XLSX.writeFile(wb, `ceviriler_${dil.dilKodu}.xlsx`);
    };

    const excelYukle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dosya = e.target.files?.[0];
        if (!dosya) return;
        setExcelYukleniyor(true);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = new Uint8Array(ev.target?.result as ArrayBuffer);
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
                // İlk satır başlık, 2. sütun Türkçe, 3. sütun hedef dil
                const map = new Map<string, string>();
                for (let i = 1; i < rows.length; i++) {
                    const anahtar = (rows[i][0] || '').toString().trim();
                    const hedef = (rows[i][2] || '').toString().trim();
                    if (anahtar) map.set(anahtar, hedef);
                }
                let guncellenen = 0;
                setEditorSatirlar(prev => prev.map(s => {
                    if (map.has(s.anahtar)) {
                        const yeniDeger = map.get(s.anahtar)!;
                        if (yeniDeger !== s.hedefMetin) guncellenen++;
                        return { ...s, hedefMetin: yeniDeger, cevrildi: yeniDeger.trim() !== '' };
                    }
                    return s;
                }));
                toastGoster('ok', `Excel yüklendi. ${guncellenen} satır güncellendi.`);
            } catch {
                toastGoster('hata', 'Excel dosyası okunamadı.');
            } finally {
                setExcelYukleniyor(false);
                if (excelInputRef.current) excelInputRef.current.value = '';
            }
        };
        reader.readAsArrayBuffer(dosya);
    };

    const yeniDilKaydet = async () => {
        if (!yeniDilKodu.trim() || !yeniDilAdi.trim()) {
            toastGoster('hata', 'Dil kodu ve adı zorunludur.');
            return;
        }
        try {
            await axiosInstance.post('/api/Ceviriler/diller', {
                dilKodu: yeniDilKodu.trim().toLowerCase(),
                dilAdi: yeniDilAdi.trim(),
                siraNo: yeniDilSiraNo,
            });
            toastGoster('ok', `${yeniDilAdi} dili eklendi.`);
            setYeniDilFormu(false);
            setYeniDilKodu('');
            setYeniDilAdi('');
            dilleriYukle();
        } catch (err: any) {
            toastGoster('hata', err?.response?.data?.mesaj || 'Dil eklenemedi.');
        }
    };

    const dilSil = async (dil: Dil) => {
        if (!window.confirm(`"${dil.dilAdi}" dilini silmek istediğinizden emin misiniz?\nTüm çeviriler de silinecektir.`)) return;
        try {
            await axiosInstance.delete(`/api/Ceviriler/diller/${dil.dilID}`);
            toastGoster('ok', `${dil.dilAdi} silindi.`);
            if (seciliDilID === dil.dilID) setSeciliDilID(null);
            dilleriYukle();
        } catch (err: any) {
            toastGoster('hata', err?.response?.data?.mesaj || 'Silinemedi.');
        }
    };

    const aktifDilDegistir = async (dilKodu: string) => {
        await dilDegistir(dilKodu);
        window.location.reload();
    };

    const gorunenSatirlar = editorSatirlar.filter(s => {
        if (filtre === 'eksik' && s.cevrildi) return false;
        if (aramaMetni && !s.turkceMetin.toLowerCase().includes(aramaMetni.toLowerCase()) &&
            !s.anahtar.toLowerCase().includes(aramaMetni.toLowerCase())) return false;
        return true;
    });

    const cevrilmis = editorSatirlar.filter(s => s.cevrildi).length;
    const toplam = editorSatirlar.length;

    return (
        <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
            {/* Toast */}
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 9999,
                    background: toast.tip === 'ok' ? '#166534' : '#991b1b',
                    color: '#fff', padding: '10px 18px', borderRadius: 8,
                    fontSize: 13.5, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.18)'
                }}>{toast.mesaj}</div>
            )}

            {/* Başlık + Yeni Dil butonu */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Dil Yönetimi</div>
                    <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                        Uygulama dillerini ve çevirilerini yönetin
                    </div>
                </div>
                <button onClick={() => setYeniDilFormu(true)}
                    style={{ padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><line x1="7" y1="1" x2="7" y2="13" stroke="#fff" strokeWidth={2} strokeLinecap="round"/><line x1="1" y1="7" x2="13" y2="7" stroke="#fff" strokeWidth={2} strokeLinecap="round"/></svg>
                    Yeni Dil Ekle
                </button>
            </div>

            {/* Yeni Dil Formu */}
            {yeniDilFormu && (
                <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#9a3412', marginBottom: 12 }}>Yeni Dil Ekle</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Dil Kodu</label>
                            <input value={yeniDilKodu} onChange={e => setYeniDilKodu(e.target.value)}
                                placeholder="en, de, fr..."
                                style={{ width: 90, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 160 }}>
                            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Dil Adı</label>
                            <input value={yeniDilAdi} onChange={e => setYeniDilAdi(e.target.value)}
                                placeholder="İngilizce, Almanca..."
                                style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Sıra No</label>
                            <input type="number" value={yeniDilSiraNo} onChange={e => setYeniDilSiraNo(Number(e.target.value))}
                                style={{ width: 70, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                            <button onClick={yeniDilKaydet}
                                style={{ padding: '7px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                Kaydet
                            </button>
                            <button onClick={() => setYeniDilFormu(false)}
                                style={{ padding: '7px 14px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Dil Listesi */}
            {yukleniyor ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>Yükleniyor...</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {diller.map((dil, idx) => {
                        const isSecili = seciliDilID === dil.dilID;
                        const isAktifDil = aktifDil === dil.dilKodu;
                        return (
                            <div key={dil.dilID}>
                                {/* Dil Satırı */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '12px 16px',
                                    background: isSecili ? '#fff7ed' : '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderTop: idx === 0 ? '1px solid #e2e8f0' : 'none',
                                    borderRadius: idx === 0 ? '10px 10px 0 0' : (idx === diller.length - 1 && !isSecili ? '0 0 10px 10px' : '0'),
                                }}>
                                    {/* Bayrak/İkon */}
                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: dil.varsayilan ? '#1a2340' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <span style={{ fontSize: 13, fontWeight: 800, color: dil.varsayilan ? '#fff' : '#64748b' }}>{dil.dilKodu.toUpperCase()}</span>
                                    </div>

                                    {/* Ad + Bilgi */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{dil.dilAdi}</span>
                                            {dil.varsayilan && (
                                                <span style={{ fontSize: 10.5, fontWeight: 700, background: '#1a2340', color: '#fff', borderRadius: 5, padding: '2px 7px' }}>Varsayılan</span>
                                            )}
                                            {isAktifDil && (
                                                <span style={{ fontSize: 10.5, fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: 5, padding: '2px 7px' }}>Aktif</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                                            {dil.varsayilan
                                                ? `${dil.ceviriSayisi} anahtar tanımlı`
                                                : `${dil.ceviriSayisi} / ${diller.find(d => d.varsayilan)?.ceviriSayisi ?? '?'} çevrildi`
                                            }
                                            {!dil.varsayilan && diller.find(d => d.varsayilan) && (
                                                <span style={{ marginLeft: 8, color: '#cbd5e1' }}>
                                                    ({Math.round(dil.ceviriSayisi / (diller.find(d => d.varsayilan)?.ceviriSayisi || 1) * 100)}%)
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Butonlar */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                        {!isAktifDil && (
                                            <button onClick={() => aktifDilDegistir(dil.dilKodu)}
                                                title="Bu dile geç"
                                                style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: 6, cursor: 'pointer' }}>
                                                Geç
                                            </button>
                                        )}
                                        {!dil.varsayilan && (
                                            <>
                                                <button onClick={() => editorAc(dil.dilID)}
                                                    style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, background: isSecili ? '#cd6906' : '#f8fafc', color: isSecili ? '#fff' : '#334155', border: `1px solid ${isSecili ? '#cd6906' : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer' }}>
                                                    {isSecili ? 'Kapat' : 'Düzenle'}
                                                </button>
                                                <button onClick={() => dilSil(dil)}
                                                    title="Dili sil"
                                                    style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
                                                    <svg width={12} height={12} viewBox="0 0 14 14" fill="none"><line x1="1" y1="1" x2="13" y2="13" stroke="currentColor" strokeWidth={2} strokeLinecap="round"/><line x1="13" y1="1" x2="1" y2="13" stroke="currentColor" strokeWidth={2} strokeLinecap="round"/></svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Editör Paneli */}
                                {isSecili && (
                                    <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: idx === diller.length - 1 ? '0 0 10px 10px' : 0, background: '#fafafa' }}>
                                        {editorYukleniyor ? (
                                            <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Çeviriler yükleniyor...</div>
                                        ) : (
                                            <>
                                                {/* Editör Araç Çubuğu */}
                                                <input
                                                    ref={excelInputRef}
                                                    type="file"
                                                    accept=".xlsx,.xls"
                                                    onChange={excelYukle}
                                                    style={{ display: 'none' }}
                                                />
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexWrap: 'wrap' }}>
                                                    <input
                                                        value={aramaMetni}
                                                        onChange={e => setAramaMetni(e.target.value)}
                                                        placeholder="Metin ara..."
                                                        style={{ flex: 1, minWidth: 160, padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }}
                                                    />
                                                    <button onClick={() => setFiltre('tumu')}
                                                        style={{ padding: '6px 14px', fontSize: 12.5, fontWeight: 600, background: filtre === 'tumu' ? '#1a2340' : '#f1f5f9', color: filtre === 'tumu' ? '#fff' : '#64748b', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                                                        Tümü ({toplam})
                                                    </button>
                                                    <button onClick={() => setFiltre('eksik')}
                                                        style={{ padding: '6px 14px', fontSize: 12.5, fontWeight: 600, background: filtre === 'eksik' ? '#dc2626' : '#f1f5f9', color: filtre === 'eksik' ? '#fff' : '#64748b', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                                                        Eksik ({toplam - cevrilmis})
                                                    </button>
                                                    <div style={{ marginLeft: 'auto', fontSize: 12.5, color: '#64748b', fontWeight: 600 }}>
                                                        {cevrilmis}/{toplam} çevrildi
                                                        <span style={{ marginLeft: 6, color: cevrilmis === toplam ? '#16a34a' : '#f59e0b' }}>
                                                            ({Math.round(cevrilmis / (toplam || 1) * 100)}%)
                                                        </span>
                                                    </div>
                                                    {/* Excel butonları */}
                                                    <button onClick={excelIndir}
                                                        title="Tüm çevirileri Excel olarak indir"
                                                        style={{ padding: '6px 13px', fontSize: 12.5, fontWeight: 600, background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        <i className="fas fa-file-excel" style={{ fontSize: 12 }} />
                                                        İndir
                                                    </button>
                                                    <button onClick={() => excelInputRef.current?.click()} disabled={excelYukleniyor}
                                                        title="Excel dosyasından çevirileri yükle"
                                                        style={{ padding: '6px 13px', fontSize: 12.5, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 7, cursor: excelYukleniyor ? 'not-allowed' : 'pointer', opacity: excelYukleniyor ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        <i className="fas fa-upload" style={{ fontSize: 12 }} />
                                                        {excelYukleniyor ? 'Yükleniyor...' : 'Yükle'}
                                                    </button>
                                                    <button onClick={kaydet} disabled={kayitYapiliyor}
                                                        style={{ padding: '6px 18px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: kayitYapiliyor ? 'not-allowed' : 'pointer', opacity: kayitYapiliyor ? 0.7 : 1 }}>
                                                        {kayitYapiliyor ? 'Kaydediliyor...' : 'Kaydet'}
                                                    </button>
                                                </div>

                                                {/* Tablo Başlığı */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, padding: '8px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Türkçe (orijinal)</div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', paddingLeft: 12 }}>
                                                        {diller.find(d => d.dilID === seciliDilID)?.dilAdi}
                                                    </div>
                                                </div>

                                                {/* Çeviri Satırları */}
                                                <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                                                    {gorunenSatirlar.length === 0 ? (
                                                        <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Gösterilecek kayıt yok.</div>
                                                    ) : gorunenSatirlar.map((satir, i) => (
                                                        <div key={satir.anahtar} style={{
                                                            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
                                                            borderBottom: i < gorunenSatirlar.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                            background: satir.cevrildi ? '#fff' : '#fffbeb',
                                                        }}>
                                                            <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid #f1f5f9' }}>
                                                                {!satir.cevrildi && (
                                                                    <span title="Çeviri eksik" style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0, display: 'inline-block' }} />
                                                                )}
                                                                <span style={{ fontSize: 13, color: '#334155' }}>{satir.turkceMetin}</span>
                                                            </div>
                                                            <div style={{ padding: '6px 16px 6px 12px' }}>
                                                                <input
                                                                    value={satir.hedefMetin}
                                                                    onChange={e => hedefMetinGuncelle(satir.anahtar, e.target.value)}
                                                                    placeholder="Çeviri girin..."
                                                                    style={{
                                                                        width: '100%', padding: '5px 8px', fontSize: 13,
                                                                        border: `1.5px solid ${satir.cevrildi ? '#e2e8f0' : '#fde68a'}`,
                                                                        borderRadius: 6, outline: 'none', boxSizing: 'border-box',
                                                                        background: satir.cevrildi ? '#fff' : '#fffef0',
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Alt Kaydet */}
                                                {gorunenSatirlar.length > 10 && (
                                                    <div style={{ padding: '12px 16px', textAlign: 'right', borderTop: '1px solid #e2e8f0' }}>
                                                        <button onClick={kaydet} disabled={kayitYapiliyor}
                                                            style={{ padding: '8px 20px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: kayitYapiliyor ? 'not-allowed' : 'pointer', opacity: kayitYapiliyor ? 0.7 : 1 }}>
                                                            {kayitYapiliyor ? 'Kaydediliyor...' : 'Kaydet'}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DilYonetimi;
